import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Inbox } from 'lucide-react';
import { STATUSES, getStatusColor, getStatusLabel } from '../utils/statusColors';
import EmptyState from './illustrations/EmptyState';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const fetchBookings = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Failed to load bookings');
        setLoading(false);
      }
    }
  };

  const fetchDrivers = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrivers(response.data);
    } catch (error) {
      // Non-fatal — the rest of the dashboard still works, we just can't offer
      // the assignment dropdown until the driver list loads.
      console.error('Failed to load drivers', error);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchDrivers();
    const interval = setInterval(fetchBookings, 20000);
    return () => clearInterval(interval);
  }, []);

  const updateStatus = async (id, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.patch(
          `${API_URL}/api/bookings/${id}/status`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
      );
      // Use the server's copy (moving to pending also clears claimed_by), but
      // keep the joined driver_name the status endpoint doesn't return.
      const updated = response.data.booking;
      setBookings(bookings.map(b => (
          b.id === id ? { ...updated, driver_name: updated.claimed_by ? b.driver_name : null } : b
      )));
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const assignDriver = async (id, driverId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(
          `${API_URL}/api/admin/bookings/${id}/assign`,
          { driver_id: driverId === '' ? null : Number(driverId) },
          { headers: { Authorization: `Bearer ${token}` } }
      );
      // The assign endpoint returns the raw row, which has no joined
      // driver_name — fill it in so the Driver row updates immediately
      // instead of reading "Unassigned" until the next poll.
      const updated = response.data.booking;
      const assigned = drivers.find(d => d.id === updated.claimed_by);
      setBookings(bookings.map(b => (
          b.id === id ? { ...updated, driver_name: assigned ? assigned.name : null } : b
      )));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to assign driver');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete booking #${id}? This cannot be undone.`)) {
      return;
    }
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(bookings.filter(b => b.id !== id));
    } catch (error) {
      alert('Failed to delete booking');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const filteredBookings = bookings.filter(b => (filter === 'all' ? true : b.status === filter));

  const stats = Object.fromEntries(
      Object.keys(STATUSES).map(status => [status, bookings.filter(b => b.status === status).length])
  );

  if (loading) {
    return (
        <div className="dashboard-page">
          <div className="loading">Loading...</div>
        </div>
    );
  }

  return (
      <div className="dashboard-page admin-compact">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div>
              <h1><LayoutDashboard size={26} className="page-icon" /> Admin Dashboard</h1>
              <p>Manage all bookings</p>
            </div>
            <div className="header-actions">
              <button onClick={handleLogout} className="logout-btn">Logout</button>
            </div>
          </div>

          <div className="filters" style={{ marginBottom: '24px' }}>
            <Link to="/admin/dashboard" className="active" style={{ textDecoration: 'none' }}>Bookings</Link>
            <Link to="/admin/drivers" style={{ textDecoration: 'none' }}>Drivers</Link>
            <Link to="/admin/calendar" style={{ textDecoration: 'none' }}>Calendar</Link>
          </div>

          <div className="stats-grid">
            {Object.keys(STATUSES).map(status => (
                <div key={status} className="stat-card" style={{ borderLeft: `4px solid ${getStatusColor(status)}` }}>
                  <div className="stat-number">{stats[status]}</div>
                  <div className="stat-label">{getStatusLabel(status)}</div>
                </div>
            ))}
          </div>

          <div className="filters">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
              All ({bookings.length})
            </button>
            {Object.keys(STATUSES).map(status => (
                <button key={status} className={filter === status ? 'active' : ''} onClick={() => setFilter(status)}>
                  {getStatusLabel(status)} ({stats[status]})
                </button>
            ))}
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="bookings-list">
            {filteredBookings.length === 0 ? (
                <EmptyState icon={Inbox} label="No bookings found" />
            ) : (
                filteredBookings.map((booking) => (
                    <div key={booking.id} className="booking-card">
                      <div className="booking-header">
                        <div className="booking-id">#{booking.id}</div>
                        <div className="booking-status" style={{ backgroundColor: getStatusColor(booking.status) }}>
                          {getStatusLabel(booking.status)}
                        </div>
                      </div>
                      <div className="booking-details">
                        <div className="detail-row">
                          <span className="label">Customer:</span>
                          <span className="value">{booking.customer_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Phone:</span>
                          <a href={`tel:${booking.customer_phone}`} className="phone-link">{booking.customer_phone}</a>
                        </div>
                        <div className="detail-row">
                          <span className="label">Pickup:</span>
                          <span className="value">{booking.pickup_location}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Destination:</span>
                          <span className="value">{booking.destination}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Time:</span>
                          <span className="value">{new Date(booking.pickup_datetime).toLocaleString()}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Driver:</span>
                          <span className={`value ${booking.driver_name ? 'driver-assigned' : 'driver-unassigned'}`}>
                            {booking.driver_name || 'Unassigned'}
                          </span>
                        </div>
                        {booking.notes && (
                            <div className="detail-row">
                              <span className="label">Notes:</span>
                              <span className="value">{booking.notes}</span>
                            </div>
                        )}
                      </div>
                      <div className="booking-actions">
                        <label className="form-label" htmlFor={`assign-${booking.id}`}>Assigned driver</label>
                        <select
                            id={`assign-${booking.id}`}
                            value={booking.claimed_by || ''}
                            onChange={(e) => assignDriver(booking.id, e.target.value)}
                            className="status-select"
                            disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                        >
                          <option value="">— Unassigned —</option>
                          {drivers
                              // Keep a deactivated driver listed while they still hold this
                              // ride, so the dropdown doesn't render blank.
                              .filter(d => d.active || d.id === booking.claimed_by)
                              .map(d => (
                                  <option key={d.id} value={d.id}>
                                    {d.name}{d.active ? '' : ' (inactive)'}
                                  </option>
                              ))}
                        </select>

                        <label className="form-label" htmlFor={`status-${booking.id}`} style={{ marginTop: '12px' }}>Status</label>
                        <select
                            id={`status-${booking.id}`}
                            value={booking.status}
                            onChange={(e) => updateStatus(booking.id, e.target.value)}
                            className="status-select"
                        >
                          {Object.keys(STATUSES).map(status => (
                              <option key={status} value={status}>{getStatusLabel(status)}</option>
                          ))}
                        </select>
                        <button className="delete-btn" onClick={() => handleDelete(booking.id)}>Delete</button>
                      </div>
                    </div>
                ))
            )}
          </div>
        </div>
      </div>
  );
}

export default AdminDashboard;